"use client";

import { formatPlainDate, parsePlainDate } from "@sa-nu/shared-utils/date";
import { useRouter } from "next/navigation";

import type { FragmentType } from "../../../__generated__/gql";
import { graphql, useFragment } from "../../../__generated__/gql";
import { getAccommodationName } from "../../../utils";

type ReservationsBoxStatus =
  | "GoToReserve"
  | "NextReservations"
  | "StayingForReservation"
  | "PastReservations"
  | null;

const Fragment = graphql(`
  fragment ReservationBox on AccommodationReservation {
    stayStatus
    firstDate
    lastDate
    reservedFrom
    provider {
      __typename
    }
    accommodation {
      name
      code
      site {
        slug
        name
        area {
          slug
        }
      }
      architectureType {
        series
      }
    }
  }
`);

export default function HeaderReservationsBox({
  reservationsData, // 現在の予約、および未来の予約
  canViewReservationCalendar,
}: {
  reservationsData: FragmentType<typeof Fragment>[];
  canViewReservationCalendar: boolean;
}) {
  const router = useRouter();

  const reservations = useFragment(Fragment, reservationsData);
  const upcomingReservations = reservations.filter(
    (r) => r.stayStatus !== "Stayed",
  );
  const featured = upcomingReservations[0];
  const badge = upcomingReservations.length;

  let status: ReservationsBoxStatus = null;
  if (upcomingReservations.length === 0 && reservations.length === 0) {
    if (canViewReservationCalendar) {
      status = "GoToReserve";
    }
  } else if (upcomingReservations.length > 0) {
    status =
      featured!.stayStatus === "Staying"
        ? "StayingForReservation"
        : "NextReservations";
  } else if (reservations.length > 0) {
    status = "PastReservations";
  }

  if (!status) {
    return <></>;
  }

  const handler = () => {
    switch (status) {
      case "NextReservations":
        router.push("/reservations");
        break;
      case "StayingForReservation":
        router.push("/reservations");
        break;
      case "PastReservations":
        router.push("/reservations");
        break;
      case "GoToReserve":
        router.push("/reservation-form");
        break;
    }
  };

  const accommodationName = featured
    ? getAccommodationName({
        provider: featured.provider,
        reservedFrom: featured.reservedFrom,
        accommodation: featured.accommodation,
      })
    : null;

  return (
    <button
      data-analytics-id="global_header_clicked_featured_reservation"
      onClick={handler}
      className="bg-slightbeige-104 relative flex grow flex-col justify-center border-l border-l-[#ECE6D8] pl-[30px] hover:opacity-60 md:w-[295px]"
    >
      {status === "NextReservations" && (
        <div className="font-prophet text-skogecha text-[10px]">次回予約</div>
      )}
      {status === "StayingForReservation" && (
        <div className="font-prophet text-skogecha text-[10px]">滞在中</div>
      )}
      {(status === "StayingForReservation" || status === "NextReservations") &&
        featured && ( // featured は必ず存在するが型システムに認識させる
          <div className="font-roboto mt-[4px] mb-[2px] inline-block border-b md:mt-[8px]">
            <span className="border-sbeige text-skogecha border-b pb-[5px]">
              <span className="text-[14px]">{accommodationName}</span>
              <span className="ml-[9px] text-[13px] font-light">
                {formatPlainDate(parsePlainDate(featured.firstDate), {
                  excludeYear: true,
                })}
                -
                {formatPlainDate(parsePlainDate(featured.lastDate), {
                  excludeYear: true,
                })}
              </span>
            </span>
          </div>
        )}
      {(status === "GoToReserve" || status === "PastReservations") && (
        <div className="mb-[4px] inline-block">
          <span className="border-sbeige text-skogecha border-b pb-[5px]">
            <span className="text-[14px]">
              {status === "GoToReserve" ? "キャビンを予約する" : "予約一覧"}
            </span>
          </span>
        </div>
      )}
      {badge > 0 && (
        <div className="bg-skogecha text-swhite absolute top-2 right-2 h-5 w-5 rounded-full pt-px text-center text-[12px]">
          {badge}
        </div>
      )}
    </button>
  );
}
